import { useHead } from '@vueuse/head'
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'

export interface SeoOptions {
  title: string
  description: string
  path?: string
  keywords?: string[]
  image?: string
  type?: string
  noindex?: boolean
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export function useSeo(options: SeoOptions) {
  const app = useAppStore()

  const canonical = computed(() => `${app.siteUrl.replace(/\/$/, '')}${options.path || ''}`)

  const fullTitle = computed(() => {
    const base = options.title
    return base.includes(app.siteName) ? base : `${base} | ${app.siteName}`
  })

  const metaTags = computed(() => {
    const tags: { name?: string; property?: string; content: string }[] = [
      { name: 'description', content: options.description },
      { property: 'og:title', content: fullTitle.value },
      { property: 'og:description', content: options.description },
      { property: 'og:type', content: options.type || 'website' },
      { property: 'og:url', content: canonical.value },
      { property: 'og:image', content: options.image || `${app.siteUrl}/og-image.png` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:type', content: 'image/png' },
      { property: 'og:site_name', content: app.siteName },
      { property: 'og:locale', content: 'en_ZA' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: fullTitle.value },
      { name: 'twitter:description', content: options.description },
      { name: 'twitter:image', content: options.image || `${app.siteUrl}/og-image.png` },
    ]
    if (options.keywords?.length) {
      tags.push({ name: 'keywords', content: options.keywords.join(', ') })
    }
    if (options.noindex) {
      tags.push({ name: 'robots', content: 'noindex' })
    }
    return tags
  })

  useHead({
    title: fullTitle,
    meta: metaTags,
    link: [{ rel: 'canonical', href: canonical }],
    script: options.jsonLd
      ? [
          {
            // Dedupe key so hydration reuses the prerendered JSON-LD element.
            key: 'json-ld',
            type: 'application/ld+json',
            children: JSON.stringify(
              Array.isArray(options.jsonLd)
                ? { '@context': 'https://schema.org', '@graph': options.jsonLd }
                : { '@context': 'https://schema.org', ...options.jsonLd }
            ),
          },
        ]
      : undefined,
  })
}
